import { Download, FileText, Loader2, StickyNote, Trash2, TriangleAlert } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { BrandContextEntry } from "./entry";
import { formatBytes, formatEntryDate } from "./format";
import type { BrandContextDocumentExtractionStatus } from "@/types/brand-context";
import { TruncatedText } from "@/components/ui/truncated-text";

interface BrandContextEntryCardProps {
  entry: BrandContextEntry;
  onOpen: () => void;
  onDelete: () => void;
  onDownload?: () => void;
  isDownloading?: boolean;
}

function ExtractionStatusBadge({ status }: { status: BrandContextDocumentExtractionStatus }) {
  if (status === "failed") {
    return (
      <Badge variant="outline" className="text-xs gap-1 text-destructive border-destructive/30 shrink-0">
        <TriangleAlert className="h-3 w-3" />
        Extraction failed
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-xs gap-1 text-muted-foreground shrink-0">
      <Loader2 className="h-3 w-3 animate-spin" />
      Processing
    </Badge>
  );
}

export function BrandContextEntryCard({ entry, onOpen, onDelete, onDownload, isDownloading = false }: BrandContextEntryCardProps) {
  return (
    <Card variant="interactive" onClick={onOpen} className="p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${
              entry.type === "document" ? "bg-primary-tint-soft" : "bg-status-info-soft"
            }`}
          >
            {entry.type === "document" ? (
              <FileText className="h-4 w-4 text-primary" />
            ) : (
              <StickyNote className="h-4 w-4 text-foreground" />
            )}
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-foreground">
              <TruncatedText>{entry.title}</TruncatedText>
            </h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              {entry.category} · {formatEntryDate(entry.createdAt)}
              {entry.authorName ? ` · ${entry.authorName}` : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {entry.type === "document" && onDownload && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={(e) => {
                e.stopPropagation();
                onDownload();
              }}
              disabled={isDownloading}
              aria-label={`Download ${entry.title}`}
            >
              {isDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive hover:text-destructive"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            aria-label={`Delete ${entry.title}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {entry.type === "learning" ? (
        <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{entry.body}</p>
      ) : (
        <div className="text-sm text-muted-foreground rounded-lg border border-border bg-surface-tint-soft px-3 py-2 flex items-center justify-between gap-2">
          <TruncatedText>
            {entry.fileName} · {formatBytes(entry.fileSize)}
          </TruncatedText>
          {entry.extractionStatus !== "done" && <ExtractionStatusBadge status={entry.extractionStatus} />}
        </div>
      )}

      {entry.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {entry.tags.map((tag) => (
            <Badge key={tag} variant="secondary" className="bg-muted">
              {tag}
            </Badge>
          ))}
        </div>
      )}
    </Card>
  );
}
